import React, { useCallback, useEffect, useRef } from 'react';
import classNames from 'classnames';

import { StyledOcticon, Timeline, Tooltip } from '@primer/react';

import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList } from 'react-window';

import { formatISOAsRelative } from '../../../utils/date_utils';
import { truncateText } from '../../../utils/text_utils';

import ClipboardLabel from '../../common/ClipboardLabel/ClipboardLabel';
import CodeFormatLabel from '../../common/Labels/CodeFormatLabel';
import CollapsibleCardView from '../../common/CardView/CollapsibleCardView';
import ErrorLabel from '../../common/Labels/ErrorLabel';
import IndexIcon from '../../common/IndexIcon/IndexIcon';
import Label from '../../common/Labels/Label';
import LoadingSpinner from '../../common/LoadingSpinner/LoadingSpinner';
import StepIcon from '../../common/icons/StepIcon';
import VersionIcon from '../../common/icons/VersionIcon';
import VerticalGradientOverlay from '../../common/overlays/VerticalGradientOverlay';
import Button from '../../common/Button/Button';

import getDownloadOrchestrationURL from '../../../utils/api_functions/downloadOrchestration';

const IndexedIcon = ({ index, ...rest }) => <IndexIcon index={index} {...rest} className="w-full h-full" />

const buttonStyle = {
  padding: 0,
  border: 'none',
  boxShadow: 'none',
  background: 'none',
};

const OrchestrationVersionContent = ({ orchVersion }) => (
  <div className="flex items-start justify-between">
    <div className="flex flex-col flex-start justify-center">
      <Label bold>{orchVersion?.id}</Label>
      <Label>Created {formatISOAsRelative(orchVersion?.created_at)}</Label>
    </div>
    <div className="flex items-center">
      <Tooltip aria-label="Download orchestration zip file">
        <Button
          className="mr-4 cursor"
          sx={buttonStyle}
          as={'a'}
          href={getDownloadOrchestrationURL(orchVersion?.id)}
          target="_blank"
          rel="noopener"
        >
          <Label className="text-brand font-normal underline cursor">
            {orchVersion?.file_name}
          </Label>
        </Button>
      </Tooltip>
      <ClipboardLabel
        iconSize={16}
        labelValue={orchVersion?.sha}
        displayLabel={truncateText(orchVersion?.sha, 16)}
        LabelComponent={CodeFormatLabel}
        showClipboardIconOnHover
      />
    </div>
  </div>
);

const OrchestrationVersionItem = ({
  index, orchestrationVersion, numberOfSteps = 1,
}) => (
  <Timeline.Item className="items-center">
    <Timeline.Badge className="before:bg-border-dark">
      {/* Octicon requires an index, but cannot pass that in without defining the component inside of render */}
      {/* eslint-disable-next-line */}
        <StyledOcticon icon={() => <IndexedIcon index={index} />} />
    </Timeline.Badge>
    <Timeline.Body>
      <div className={classNames('p-1 w-full', { 'border-b-1': numberOfSteps - 1 !== index })}>
        <OrchestrationVersionContent orchVersion={orchestrationVersion} />
      </div>
    </Timeline.Body>
  </Timeline.Item>
)

const OrchestrationVersionRow = ({ data, index, style }) => {
  const rowRef = useRef({});
  const {
    listRef,
    rowHeights,
    items,
    numberOfSteps,
  } = data
  const setRowHeight = (rowIndex, size) => {
    listRef?.current?.resetAfterIndex(0);
    rowHeights.current = { ...rowHeights.current, [rowIndex]: size }
  };

  useEffect(() => {
    if (rowRef.current) {
      setRowHeight(index, rowRef.current.clientHeight);
    }
    // Excludes setRowHeight from dependencies
    // eslint-disable-next-line
  }, [index]);

  return (
    <div style={style}>
      <OrchestrationVersionItem
        key={items[index].id}
        orchestrationVersion={items[index]}
        numberOfSteps={numberOfSteps}
        index={index}
      />
    </div>
  )
};

export const OrchestrationTimelineContent = ({
  items,
  error,
  height = 240,
  disableScrollFading = false,
}) => {
  const listRef = useRef({});
  const rowHeights = useRef({});

  const numberOfSteps = items?.length || 0;

  const noDataMessage = error
    ? 'There was an error retrieving orchestration history...'
    : 'No history to show for this orchestration instance...'

  const getRowHeight = useCallback((index) => {
    const rowHeight = rowHeights?.current[index]
    return rowHeight || 76;
  }, [rowHeights]);

  if (!items?.length) {
    return null;
  }

  return !numberOfSteps ? (
    <div className="p-4 flex-grow flex flex-col justify-center items-center">
      <StyledOcticon
        className="mb-2 text-brand"
        icon={StepIcon}
        size={16}
      />
      <ErrorLabel>{noDataMessage}</ErrorLabel>
    </div>
  ) : (
    <div className="overflow-scroll px-6 flex-grow relative">
      {/* This zIndex override creates a stacking context, which prevents the Overlays's zIndex: 20 from appearing over root components. */}
      <Timeline sx={{ zIndex: 1 }}>
        {
          !disableScrollFading && (
            <VerticalGradientOverlay className="top-0 left-0 right-0" fromDirection="b" />
          )
        }
        <AutoSizer disableHeight>
          {({ width }) => (
            items?.length && (
              <VariableSizeList
                ref={listRef}
                height={height}
                itemCount={numberOfSteps}
                estimatedItemSize={76}
                itemSize={getRowHeight}
                width={width}
                itemData={{
                  listRef,
                  rowHeights,
                  items
                }}
              >
                {OrchestrationVersionRow}
              </VariableSizeList>
            )
          )}
        </AutoSizer>
        {
          !disableScrollFading && (
            <VerticalGradientOverlay className="bottom-0 left-0 right-0" fromDirection="t" />
          )
        }
      </Timeline>
    </div>
  )
}

export const OrchestrationTimeline = ({
  orchestrationHistory = [],
  error,
  loading,
  timelineTitle = 'Workload History',
  disableScrollFading = false,
}) => {
  if (loading) {
    return <LoadingSpinner animationKey="orchestration-timeline-loader" useOverlay />;
  }

  return (
    <CollapsibleCardView
      disableFullHeight
      headerText={timelineTitle} icon={VersionIcon}
      fixedHeight="240px"
    >
      <OrchestrationTimelineContent
        items={orchestrationHistory}
        error={error}
        disableScrollFading={disableScrollFading}
      />
    </CollapsibleCardView>
  );
};

export default OrchestrationTimeline;
